import { createContext, useContext, useReducer, useMemo, useImperativeHandle } from 'react';

import {
    NavigationContextType,
    NavigationUpdateContextType,
    ScreenType,
    ParamsType,
    NavigationProviderProps,
} from './NavigatorContext.interface';
import reducer from './reducer';

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);
export const NavigationUpdateContext = createContext<NavigationUpdateContextType | undefined>(undefined);

export function useNavigationState(): NavigationContextType {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigationState must be used within a NavigationProvider');
    }
    return context;
}

export function useNavigationUpdate(): NavigationUpdateContextType {
    const context = useContext(NavigationUpdateContext);
    if (!context) {
        throw new Error('useNavigationUpdate must be used within a NavigationProvider');
    }
    return context;
}

export function NavigationProvider({ children, navigationRef }: NavigationProviderProps) {
    const [state, dispatch] = useReducer(reducer, { stack: [] });

    const navigationValue = useMemo<NavigationContextType>(() => {
        const currentScreenData = state.stack[state.stack.length - 1];
        return {
            stack: state.stack,
            currentScreen: currentScreenData?.screen,
            params: currentScreenData?.params || {},
        };
    }, [state]);

    const updateValue = useMemo<NavigationUpdateContextType>(
        () => ({
            navigateToPage: (screen: ScreenType, params: ParamsType = {}) =>
                dispatch({ type: 'NAVIGATE', screen, params }),
            goBack: () => dispatch({ type: 'GO_BACK' }),
            push: (screen: ScreenType, params: ParamsType = {}) => dispatch({ type: 'PUSH', screen, params }),
            pop: () => dispatch({ type: 'POP' }),
            clean: () => dispatch({ type: 'CLEAN' }),
        }),
        [dispatch]
    );

    useImperativeHandle(navigationRef, () => updateValue, [updateValue]);

    return (
        <NavigationContext.Provider value={navigationValue}>
            <NavigationUpdateContext.Provider value={updateValue}>{children}</NavigationUpdateContext.Provider>
        </NavigationContext.Provider>
    );
}
